import React, {
  forwardRef, useMemo, useCallback, useRef,
} from 'react';
import useSetState from '#utils/use-set-state';
import MuiAlert, { AlertColor, AlertProps } from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';

import {
  ALERT_SEVERITY,
} from '#utils/constants';
import { translateWearDateError } from '#utils/wear-date-error';
import type { WearDateError } from '#common/validation/order';

interface DialogData {
  title?: React.ReactNode;
  text?: React.ReactNode;
  agree?: string;
  disagree?: string;
  onAgree?: () => void;
  onDisagree?: () => void;
  handleClose?: () => void;
}

interface ModalData {
  style?: SxProps<Theme>;
  children?: React.ReactNode;
}

interface FeedbackUIState {
  backdropOpen: boolean;
  snackbarOpen: boolean;
  snackbarMessage: React.ReactNode;
  snackbarMessageSeverity: AlertColor;
  dialogOpen: boolean;
  dialogData: DialogData;
  modalOpen: boolean;
  modalData: ModalData;
  backdropVideoUrl: string | null;
}

interface SimpleSnackbarResult {
  error?: {
    data?: { message?: string; wearDateError?: WearDateError };
    error?: string;
  };
}

export interface FeedbackUIContextValue {
  showBackdrop: () => void;
  hideBackdrop: () => void;
  showSnackbar: (message: React.ReactNode, severity?: AlertColor) => void;
  simpleSnackbar: (res: SimpleSnackbarResult, successMessage?: string) => SimpleSnackbarResult['error'];
  showDialog: () => void;
  hideDialog: () => void;
  setDialogData: (dialogData: DialogData) => void;
  showModal: () => void;
  hideModal: () => void;
  setModalData: (modalData: ModalData) => void;
}

const modalStyle: SxProps<Theme> = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 800,
  width: '100%',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
};

const Alert = forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
Alert.displayName = 'FeedbackProvider';

export function FeedbackUIProvider(props: { children?: React.ReactNode }) {
  const [state, setState] = useSetState<FeedbackUIState>({
    backdropOpen: false,
    snackbarOpen: false,
    snackbarMessage: '',
    snackbarMessageSeverity: 'success',
    dialogOpen: false,
    // dialogTitle: '',
    // dialogText: '',
    dialogData: {},
    modalOpen: false,
    modalData: {},
    backdropVideoUrl: null,
  });

  // Focus the dialog's primary action once it has finished opening, instead of
  // the DOM `autoFocus` attribute (jsx-a11y/no-autofocus).
  const agreeButtonRef = useRef<HTMLButtonElement>(null);

  const setSnackbarOpen = (snackbarOpen: boolean) => setState({ snackbarOpen });
  const setSnackbarMessage = (snackbarMessage: React.ReactNode) => setState({ snackbarMessage });
  const setSnackbarMessageSeverity = (snackbarMessageSeverity: AlertColor) => setState({ snackbarMessageSeverity });
  const setDialogOpen = (dialogOpen: boolean) => setState({ dialogOpen });
  const setDialogData = (dialogData: DialogData) => setState({ dialogData });
  const setModalOpen = (modalOpen: boolean) => setState({ modalOpen });
  const setModalData = (modalData: ModalData) => setState({ modalData });

  const showBackdrop = useCallback(() => {
    setState({ backdropOpen: true });
  }, [setState]);

  const hideBackdrop = useCallback(() => {
    setState({ backdropOpen: false, backdropVideoUrl: null });
  }, [setState]);

  const showSnackbar = (message: React.ReactNode, severity: AlertColor = 'success') => {
    setSnackbarMessage(message);
    setSnackbarMessageSeverity(severity);
    setSnackbarOpen(true);
  };

  const showDialog = () => {
    setDialogOpen(true);
  };

  const hideDialog = () => {
    setDialogOpen(false);
  };

  const showModal = () => {
    setModalOpen(true);
  };

  const hideModal = () => {
    setModalOpen(false);
  };

  const simpleSnackbar = (res: SimpleSnackbarResult, successMessage?: string) => {
    if (res.error) {
      const { data } = res.error;
      let errorText = data ? data.message : res.error.error;
      if (data?.wearDateError) {
        errorText = translateWearDateError(data.wearDateError);
      }
      showSnackbar(true);
      setSnackbarMessage(errorText);
      setSnackbarMessageSeverity(ALERT_SEVERITY.ERROR as AlertColor);
    } else if (successMessage) {
      showSnackbar(true);
      setSnackbarMessage(successMessage);
      setSnackbarMessageSeverity(ALERT_SEVERITY.SUCCESS as AlertColor);
    }

    return res.error;
  };

  const providerValues = useMemo<FeedbackUIContextValue>(() => ({
    showBackdrop,
    hideBackdrop,

    showSnackbar,
    simpleSnackbar,

    showDialog,
    hideDialog,
    setDialogData,

    showModal,
    hideModal,
    setModalData,
  }), [showBackdrop, hideBackdrop]);

  return (
    <FeedbackUIContext.Provider value={providerValues}>
      {props.children}

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={state.backdropOpen}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog
        open={state.dialogOpen}
        onClose={state.dialogData.handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        slotProps={{
          transition: {
            onEntered: () => agreeButtonRef.current?.focus(),
          },
        }}
      >
        <DialogTitle id="alert-dialog-title">
          {state.dialogData.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ whiteSpace: 'pre-wrap' }}>
            {state.dialogData.text}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {
            state.dialogData.disagree
            && (
              <Button onClick={state.dialogData.onDisagree}>
                {state.dialogData.disagree}
              </Button>
            )
          }
          <Button ref={agreeButtonRef} onClick={state.dialogData.onAgree}>
            {state.dialogData.agree || 'OK'}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal
        open={state.modalOpen}
        onClose={hideModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        disablePortal
      >
        <Box sx={state.modalData.style || modalStyle}>
          {state.modalData.children}
        </Box>
      </Modal>

      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={state.snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={state.snackbarMessageSeverity}
          sx={{ width: '100%' }}
        >
          {state.snackbarMessage}
        </Alert>
      </Snackbar>
    </FeedbackUIContext.Provider>
  );
}

export const FeedbackUIContext = React.createContext<FeedbackUIContextValue | null>(null);
